import { defaultTemplateSpec } from './templateSpecUtils';

const DESKTOP_BASE_URL = 'http://127.0.0.1:17890';
const INDEX_FILE = 'index.html';
const STYLE_FILE = 'style.css';

const defaultFileHtml = () => defaultTemplateSpec().html;
const defaultFileCss = () => defaultTemplateSpec().css ?? '';

const desktopFetch = async (path, options = {}) => {
  const response = await fetch(`${DESKTOP_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    const message = data?.error || `Desktop request failed (${response.status})`;
    throw new Error(message);
  }
  return data;
};

export const pingDesktop = async () => {
  try {
    const data = await desktopFetch('/api/ping', { method: 'GET' });
    return !!data?.ok;
  } catch {
    return false;
  }
};

export const isBrowserFolderPickerSupported = () => (
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function'
);

export const pickCatalogTemplateFolder = async () => {
  const data = await desktopFetch('/api/catalog-template/pick-folder', { method: 'POST' });
  return String(data?.path || '').trim();
};

export const readCatalogTemplateFiles = async (folderPath) => {
  const path = String(folderPath || '').trim();
  if (!path) {
    throw new Error('Folder path is required');
  }
  const query = new URLSearchParams({ path });
  const data = await desktopFetch(`/api/catalog-template/files?${query.toString()}`, { method: 'GET' });
  return {
    path: String(data?.path || path),
    html: String(data?.html ?? ''),
    css: String(data?.css ?? ''),
  };
};

export const writeCatalogTemplateFiles = async ({ path, html, css }) => {
  const folderPath = String(path || '').trim();
  if (!folderPath) {
    throw new Error('Folder path is required');
  }
  await desktopFetch('/api/catalog-template/files', {
    method: 'PUT',
    body: JSON.stringify({ path: folderPath, html: html ?? '', css: css ?? '' }),
  });
};

export const folderDisplayName = (folderPath) => {
  const normalized = String(folderPath || '').replace(/\\/g, '/').replace(/\/+$/, '');
  if (!normalized) return '';
  const parts = normalized.split('/');
  return parts[parts.length - 1] || normalized;
};

const readTextFromDirHandle = async (dirHandle, fileName) => {
  try {
    const fileHandle = await dirHandle.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return file.text();
  } catch (err) {
    if (err?.name === 'NotFoundError') {
      return null;
    }
    throw err;
  }
};

const writeTextToDirHandle = async (dirHandle, fileName, contents) => {
  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(contents ?? '');
  await writable.close();
};

const ensureTextInDirHandle = async (dirHandle, fileName, defaultContent) => {
  const existing = await readTextFromDirHandle(dirHandle, fileName);
  if (existing !== null) {
    return String(existing);
  }
  await writeTextToDirHandle(dirHandle, fileName, defaultContent);
  return defaultContent;
};

const ensureBrowserCatalogTemplateFiles = async (dirHandle) => {
  const html = await ensureTextInDirHandle(dirHandle, INDEX_FILE, defaultFileHtml());
  const css = await ensureTextInDirHandle(dirHandle, STYLE_FILE, defaultFileCss());
  return { html, css };
};

export const pickBrowserCatalogTemplateFolder = async () => {
  if (!isBrowserFolderPickerSupported()) {
    return null;
  }
  let dirHandle;
  try {
    dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (err) {
    if (err?.name === 'AbortError') {
      return null;
    }
    throw err;
  }
  const { html, css } = await ensureBrowserCatalogTemplateFiles(dirHandle);
  return {
    source: 'browser',
    folderPath: dirHandle.name,
    folderName: dirHandle.name,
    html,
    css,
    dirHandle,
  };
};

export const readBrowserCatalogTemplateFiles = async (dirHandle) => {
  if (!dirHandle) {
    throw new Error('Folder handle is required');
  }
  return ensureBrowserCatalogTemplateFiles(dirHandle);
};

export const writeBrowserCatalogTemplateFiles = async (dirHandle, { html, css }) => {
  if (!dirHandle) {
    throw new Error('Folder handle is required');
  }
  await writeTextToDirHandle(dirHandle, INDEX_FILE, html ?? '');
  await writeTextToDirHandle(dirHandle, STYLE_FILE, css ?? '');
};

export const openCatalogTemplateFolder = async () => {
  const desktopOnline = await pingDesktop();
  if (desktopOnline) {
    const folderPath = await pickCatalogTemplateFolder();
    if (!folderPath) {
      return null;
    }
    const files = await readCatalogTemplateFiles(folderPath);
    return {
      source: 'desktop',
      folderPath: files.path || folderPath,
      folderName: folderDisplayName(folderPath),
      html: files.html,
      css: files.css,
      dirHandle: null,
    };
  }

  if (!isBrowserFolderPickerSupported()) {
    return { unavailable: true };
  }

  return pickBrowserCatalogTemplateFolder();
};

export const saveCatalogTemplateSession = async (session, { html, css }) => {
  if (!session) {
    throw new Error('Session is required');
  }
  if (session.source === 'browser') {
    await writeBrowserCatalogTemplateFiles(session.dirHandle, { html, css });
    return;
  }
  await writeCatalogTemplateFiles({
    path: session.folderPath,
    html,
    css,
  });
};

export const isValidFileEditSession = (state) => {
  if (!state) return false;
  if (state.source === 'browser') {
    return !!state.dirHandle;
  }
  return !!String(state.folderPath || '').trim();
};
